import { Minus, ArrowUp, ArrowDown, RefreshCw, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Checkbox } from '../../ui/checkbox'
import {
  PriceAdjustment,
  PriceAdjustmentProduct,
} from '../../../services/purchases.service'

interface PriceAdjustmentModalProps {
  showPriceAdjustmentModal: boolean
  setShowPriceAdjustmentModal: (show: boolean) => void
  selectedAdjustment: PriceAdjustment | null
  editableAdjustmentProducts: PriceAdjustmentProduct[]
  setEditableAdjustmentProducts: (products: PriceAdjustmentProduct[]) => void
  keepPreviousPrice: { [key: number]: boolean }
  setKeepPreviousPrice: (state: { [key: number]: boolean }) => void
}

export function PriceAdjustmentModal({
  showPriceAdjustmentModal,
  setShowPriceAdjustmentModal,
  selectedAdjustment,
  editableAdjustmentProducts,
  setEditableAdjustmentProducts,
  keepPreviousPrice,
  setKeepPreviousPrice,
}: PriceAdjustmentModalProps) {
  return (
    <Dialog
      open={showPriceAdjustmentModal}
      onOpenChange={setShowPriceAdjustmentModal}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>Detalle de Ajuste de Precio</DialogTitle>
          <DialogDescription className="sr-only">
            Detalles de los productos con cambios de precio
          </DialogDescription>
        </DialogHeader>
        {selectedAdjustment && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-4 rounded-lg bg-slate-50 p-4">
              <div>
                <p className="text-sm text-slate-600">ID:</p>
                <p className="font-semibold text-slate-900">
                  {selectedAdjustment.id}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Proveedor:</p>
                <p className="font-semibold text-slate-900">
                  {selectedAdjustment.supplier}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Fecha:</p>
                <p className="font-semibold text-slate-900">
                  {selectedAdjustment.date}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">ID Compra:</p>
                <p className="font-semibold text-slate-900">
                  {selectedAdjustment.purchaseId}
                </p>
              </div>
            </div>

            {/* Products Table */}
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Mantener Anterior
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Cant.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Costo Anterior
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Costo Nuevo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Cambio
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Precio Venta Anterior
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Utilidad % Anterior
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Precio Venta Nuevo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Utilidad % Nuevo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Lote
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {editableAdjustmentProducts.map((product, index) => {
                    const keepPrevious = keepPreviousPrice[product.id] || false
                    const displaySalePrice = keepPrevious
                      ? product.previousSalePrice
                      : product.newSalePrice
                    const displayMargin = keepPrevious
                      ? product.previousMargin
                      : product.newMargin

                    return (
                      <tr key={product.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={keepPrevious}
                              onCheckedChange={(checked) => {
                                setKeepPreviousPrice({
                                  ...keepPreviousPrice,
                                  [product.id]: checked as boolean,
                                })
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">
                            {product.name}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-900">
                            {product.quantity}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-slate-700">
                            ${product.previousCost.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-slate-900">
                            ${product.newCost.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {product.costChange === 0 ? (
                            <div className="flex items-center gap-1 text-slate-600">
                              <Minus className="h-4 w-4" />
                              <span className="text-sm font-medium">0%</span>
                            </div>
                          ) : product.costChange > 0 ? (
                            <div className="flex items-center gap-1 text-red-600">
                              <ArrowUp className="h-4 w-4" />
                              <span className="text-sm font-semibold">
                                +{product.costChange.toFixed(2)}%
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-green-600">
                              <ArrowDown className="h-4 w-4" />
                              <span className="text-sm font-semibold">
                                {product.costChange.toFixed(2)}%
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-slate-700">
                            ${product.previousSalePrice.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-slate-700">
                            {product.previousMargin.toFixed(2)}%
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            step="0.01"
                            value={displaySalePrice.toFixed(2)}
                            onChange={(e) => {
                              const newPrice = parseFloat(e.target.value) || 0
                              const newMargin =
                                ((newPrice - product.newCost) /
                                  product.newCost) *
                                100
                              const updated = [...editableAdjustmentProducts]
                              updated[index] = {
                                ...product,
                                newSalePrice: newPrice,
                                newMargin: newMargin,
                              }
                              setEditableAdjustmentProducts(updated)
                            }}
                            disabled={keepPrevious}
                            className="h-8 w-24 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            step="0.01"
                            value={displayMargin.toFixed(2)}
                            onChange={(e) => {
                              const newMargin = parseFloat(e.target.value) || 0
                              const newPrice =
                                product.newCost * (1 + newMargin / 100)
                              const updated = [...editableAdjustmentProducts]
                              updated[index] = {
                                ...product,
                                newSalePrice: newPrice,
                                newMargin: newMargin,
                              }
                              setEditableAdjustmentProducts(updated)
                            }}
                            disabled={keepPrevious}
                            className="h-8 w-20 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-600">
                            {product.lotNumber}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  console.log('Aplicando ajustes de precio:', {
                    adjustment: selectedAdjustment,
                    updatedProducts: editableAdjustmentProducts,
                    keepPrevious: keepPreviousPrice,
                  })
                  setShowPriceAdjustmentModal(false)
                }}
                className="flex-1"
                size="lg"
              >
                <RefreshCw className="mr-2 h-5 w-5" />
                Aplicar Ajustes de Precio
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPriceAdjustmentModal(false)}
                className="flex-1"
                size="lg"
              >
                <X className="mr-2 h-5 w-5" />
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
